import { useState, useEffect, useRef } from 'react';
import { Reveal, SectionHead } from './UI';
import SplitText from './SplitText';
import KineticGrid from './ui/kinetic-grid';
import useReveal from '../hooks/useReveal';

const PURPLE_COLOR = '#7C3AED';
const PURPLE_BADGE_BG = '#F5F3FF';
const PURPLE_BADGE_BORDER = '#DDD6FE';

const STEPS = [
  {
    num: '01',
    label: 'Connect',
    color: PURPLE_COLOR,
    badgeBg: PURPLE_BADGE_BG,
    badgeBorder: PURPLE_BADGE_BORDER,
    body: 'A lightweight integration links TruVue AI to your existing cameras. No new hardware, no rewiring, no downtime. If a camera can output a feed, it can connect.',
  },
  {
    num: '02',
    label: 'Detect',
    color: PURPLE_COLOR,
    badgeBg: PURPLE_BADGE_BG,
    badgeBorder: PURPLE_BADGE_BORDER,
    body: 'Our models watch every feed continuously, recognising safety, compliance and security events as they unfold — across every zone, every shift.',
  },
  {
    num: '03',
    label: 'Structure',
    color: PURPLE_COLOR,
    badgeBg: PURPLE_BADGE_BG,
    badgeBorder: PURPLE_BADGE_BORDER,
    body: 'Every event is time-stamped, tagged and logged automatically — an audit-ready record with zero manual effort. Search it, export it, hand it to an inspector.',
  },
  {
    num: '04',
    label: 'Act',
    color: PURPLE_COLOR,
    badgeBg: PURPLE_BADGE_BG,
    badgeBorder: PURPLE_BADGE_BORDER,
    body: 'Your team gets instant alerts and daily reports, so issues get fixed in minutes, not rediscovered next audit. No feeds to babysit — just action when it counts.',
  },
];

function TypewriterHeading({ text = '', color = '#7C3AED', delay = 0 }) {
  const [displayedText, setDisplayedText] = useState('');
  const [ref, vis] = useReveal(0.1, '-30px');

  useEffect(() => {
    if (!vis || !text) {
      setDisplayedText('');
      return;
    }

    let intervalId = null;
    const startTimeout = setTimeout(() => {
      let currentIndex = 0;
      intervalId = setInterval(() => {
        if (currentIndex <= text.length) {
          setDisplayedText(text.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(intervalId);
        }
      }, 90);
    }, delay);

    return () => {
      clearTimeout(startTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [vis, text, delay]);

  return (
    <h3 ref={ref} className="how-label" style={{ color: color }}>
      <span>{displayedText}</span>
      <span
        style={{
          display: 'inline-block',
          width: '2px',
          height: '0.85em',
          backgroundColor: color,
          marginLeft: '4px',
          verticalAlign: 'baseline',
          animation: 'blinkCursor 0.8s infinite',
        }}
      />
    </h3>
  );
}

export default function HowItWorks() {
  return (
    <section id="how">
      <style>{`
        @keyframes blinkCursor {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        /* ── Eyebrow: strip decorative lines ── */
        #how [class*="eyebrow"]::before,
        #how [class*="eyebrow"]::after {
          content: none !important;
          display: none !important;
        }

        /* ── Override SectionHead colors for light bg ── */
        #how .section-head-eyebrow {
          color: var(--heat) !important;
        }

        #how .section-head-h2 {
          color: var(--ink) !important;
        }

        #how .section-head-p {
          color: var(--ink-soft) !important;
        }

        /* ── Container ── */
        #how .how-container {
          max-width: 1520px;
          margin: 0 auto;
          padding: clamp(64px, 8vw, 120px) clamp(16px, 3vw, 24px);
          position: relative;
          z-index: 1;
        }

        /* ── Grid ── */
        #how .how-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border: 1.5px dashed #C0B9B0;
          border-radius: 8px;
          overflow: hidden;
        }

        /* ── Card ── */
        #how .how-card {
          padding: clamp(26px, 3vw, 40px) clamp(22px, 2.5vw, 32px);
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: rgba(255,255,255,0.82);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border-right: 1.5px dashed #C0B9B0;
          transition: background 0.3s ease, transform 0.25s ease;
        }

        #how .how-card:hover {
          background: rgba(255,255,255,0.97);
        }

        #how .how-card:last-child {
          border-right: none;
        }

        /* ── Number badge ── */
        #how .how-num-badge {
          width: 46px;
          height: 46px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-mono, monospace);
          font-size: 14px;
          font-weight: 700;
          flex-shrink: 0;
          letter-spacing: 0.03em;
          transition: transform 0.2s ease;
        }

        /* ── Label ── */
        #how .how-label {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
          font-size: clamp(22px, 2.1vw, 28px);
          font-weight: 700;
          margin: 0;
          line-height: 1.2;
          min-height: 34px;
          display: flex;
          align-items: center;
        }

        /* ── Body ── */
        #how .how-body {
          font-size: clamp(14px, 1.05vw, 15.5px);
          line-height: 1.72;
          color: var(--ink-soft, #6B6760);
          margin: 0;
        }

        /* ── Tablet: 2×2 ── */
        @media (max-width: 900px) {
          #how .how-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          #how .how-card {
            border-right: 1.5px dashed #C0B9B0;
            border-bottom: 1.5px dashed #C0B9B0;
          }

          #how .how-card:nth-child(2n) {
            border-right: none;
          }

          #how .how-card:nth-last-child(-n+2) {
            border-bottom: none;
          }
        }

        /* ── Mobile: 1 col ── */
        @media (max-width: 540px) {
          #how .how-grid {
            grid-template-columns: 1fr;
          }

          #how .how-card {
            border-right: none;
            border-bottom: 1.5px dashed #C0B9B0;
            padding: 24px 18px;
          }

          #how .how-card:last-child {
            border-bottom: none;
          }
        }
      `}</style>

      <KineticGrid globalColor="light" style={{ minHeight: 'auto' }}>

        {/* Top gradient fade */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: 120,
          background: 'linear-gradient(to bottom, #ffffff 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }} />

        {/* Bottom gradient fade */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 120,
          background: 'linear-gradient(to top, #ffffff 0%, transparent 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }} />

        <div className="how-container">
          <Reveal>
            <SectionHead
              center
              eyebrow="How It Works"
              h2="From footage to action, automatically"
              p="Four steps is all it takes to turn passive CCTV into an active safety and compliance layer."
            />
          </Reveal>

          <div className="how-grid">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 80}>
                <div className="how-card">
                  <div
                    className="how-num-badge"
                    style={{
                      backgroundColor: s.badgeBg,
                      borderColor: s.badgeBorder,
                      borderStyle: 'solid',
                      borderWidth: '1px',
                      color: s.color,
                    }}
                  >
                    {s.num}
                  </div>
                  <TypewriterHeading
                    text={s.label}
                    color={s.color}
                    delay={i * 250 + 200}
                  />
                  <SplitText
                    tag="p"
                    text={s.body}
                    delay={10}
                    duration={0.7}
                    ease="power2.out"
                    splitType="words"
                    from={{ opacity: 0, y: 15 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0.1}
                    rootMargin="-40px"
                    textAlign="left"
                    className="how-body"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </KineticGrid>
    </section>
  );
}